import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import logo from "../../../../assets/images/sm/smart-apps-logo-light.png";
import logoDark from "../../../../assets/images/sm/smart-apps-logo-dark.png";
import { DEVICE_TYPES } from "../../../../constants/eventDataTypes";
import { GlobalContext } from "smart-react";
import { ErrorFallback, Loader } from "smart-react";
import NewPasswordForm from "../Components/Forms/NewPassword";
import { Alert } from "@/ui/alert";
import { AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import useIsDark from "../../Components/Theme/useIsDark";

/**
 * New Password Page (UI migrated to Tailwind + shadcn, behavior unchanged)
 * Matches the Splash two-column card layout.
 */
const NewPasswordPage: React.FC = () => {
  const { dimensions, deviceInfo } = React.useContext(
    GlobalContext as unknown as React.Context<any>
  ) as any;

  const [isMobile, setIsMobile] = React.useState<boolean | null>(null);
  const [isLoader, setIsLoader] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const isDark = useIsDark();

  React.useEffect(() => {
    if (dimensions !== undefined) {
      if (
        deviceInfo?.type === DEVICE_TYPES.PHONE ||
        deviceInfo?.type === DEVICE_TYPES.TABLET
      ) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }
  }, [dimensions, deviceInfo?.type]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <>
        {isLoader && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-background/60 backdrop-blur-sm">
            <Loader />
          </div>
        )}

        {isMobile !== null && (
          <div className="tw theme-zinc min-h-screen bg-muted/30 dark:bg-background flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-5xl rounded-2xl border border-border bg-card shadow-none ring-1 ring-inset ring-black/5 dark:ring-white/5 overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Left: form side */}
                <div className="order-2 md:order-1 p-8 sm:p-10 md:border-r">
                  <div className="mt-6 text-center">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                      Create New Password
                    </h1>
                    <p className="mt-1 text-sm text-foreground/80">
                      Set a strong password to secure your account.
                    </p>
                  </div>

                  {!!errorMessage && (
                    <Alert
                      variant="destructive"
                      role="alert"
                      className="mt-3 p-2"
                    >
                      <div className="flex items-center gap-2 text-sm min-w-0">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span className="truncate" title={errorMessage}>
                          {errorMessage}
                        </span>
                      </div>
                    </Alert>
                  )}

                  <div className="mt-6">
                    <NewPasswordForm setIsLoader={setIsLoader} />
                  </div>

                  <p className="mt-6 text-center text-sm text-muted-foreground">
                    Remembered your password?{" "}
                    <Link
                      to="/login"
                      className="text-primary underline underline-offset-4"
                    >
                      Back to sign in
                    </Link>
                  </p>
                </div>

                {/* Right: logo side */}
                <div className="order-1 md:order-2 flex items-center justify-center p-8">
                  <img
                    src={isDark ? logoDark : logo}
                    className="h-auto w-[15rem] mx-auto"
                    alt={`${process.env.APP_NAME ?? "App"} Logo`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </ErrorBoundary>
  );
};

export default NewPasswordPage;
